import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Me, type OrganizationSummary, type Competition } from '../lib/api';
import { Card } from '../components/Card';
import { HelperCard } from '../components/HelperCard';
import { HelpText } from '../components/HelpText';
import { StatusBadge } from '../components/StatusBadge';
import { CompetitionCreateForm } from '../components/CompetitionCreateForm';
import { CompetitionLifecycleControls } from '../components/CompetitionLifecycleControls';
import { ToolingShell, type Tool } from '../components/ToolingShell';
import { GuideMeLauncher } from '../components/GuideMeLauncher';
import { SetupProgressStrip, type StripItem } from '../components/SetupProgressStrip';
import { CompProgressDots } from '../components/CompProgressDots';
import { useCompChecklist, type CompChecklistItem } from '../lib/useCompChecklist';
import { useOrgChecklist, type OrgChecklistItem } from '../lib/useOrgChecklist';
import { useSectionHighlightController, useSectionHighlightContext, SectionHighlightProvider } from '../lib/sectionHighlight';
import { usePageActionNode } from '../lib/pageActions';
import { fireEdgeSparkFlourish } from '../lib/sectionSparkFlourish';
import { decodeSetupJump, type JumpableItem } from '../lib/setupJump';
import { PresenceBar } from '../components/PresenceBar';
import { OrgPanel } from '../components/OrgPanel';
import { OrgStatsPanel } from '../components/OrgStatsPanel';
import { IncomingRoleRequestsPanel } from '../components/IncomingRoleRequestsPanel';
import { NotificationDelegatesPanel } from '../components/NotificationDelegatesPanel';
import { OrgMembersPanel } from '../components/OrgMembersPanel';
import { OrgOwnerBoardPanel } from '../components/OrgOwnerBoardPanel';
import { OrgProposalsPanel } from '../components/OrgProposalsPanel';
import { OrgClansPanel } from '../components/OrgClansPanel';
import { ClanCreateRequestsPanel } from '../components/ClanCreateRequestsPanel';
import { OrgPublicPageEditor } from '../components/OrgPublicPageEditor';
import { CompPublicPageEditor } from '../components/CompPublicPageEditor';
import { CompetitionMembersPanel } from '../components/CompetitionMembersPanel';
import { ClanWorkbench } from '../components/ClanWorkbench';
import { RosterOverviewPanel } from '../components/RosterOverviewPanel';
import { TransferLeadershipPanel } from '../components/TransferLeadershipPanel';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { MatchScheduleBoard } from '../components/MatchScheduleBoard';
import { FounderBillingControls } from '../components/FounderBillingControls';
import { GuildConnectionPanel } from '../components/GuildConnectionPanel';
import { RolePolicySection } from '../components/RolePolicySection';
import { canWrite, isAdminRole } from '../lib/roles';
import { isOrgManagerOfAny, managedOrgIds, isPlatformOrgManager } from '../lib/orgAccess';
import { useEffectiveMe } from '../lib/viewAs';
import { cn } from '../lib/cn';
import { SETTLE, settleDelay } from '../lib/motion';
import {
  Rocket,
  Building2,
  Users,
  Armchair,
  Swords,
  Shield,
  ShieldCheck,
  BellRing,
  Pencil,
  Lock,
} from 'lucide-react';

// 2026 redesign: Tournament Setup converted to the Photoshop-style
// ToolingShell pattern. The six setup steps live in the left tool-strip
// as icon buttons; selecting one renders its controls in the middle
// canvas; the right inspector holds the per-step explainer + status.

type StepId = 'org' | 'competition' | 'members' | 'clans' | 'seats' | 'matches';

const TOOLS_META: { id: StepId; label: string; icon: Tool['icon'] }[] = [
  { id: 'org',         label: 'Organisation', icon: Building2 },
  { id: 'competition', label: 'Competition',  icon: Rocket },
  { id: 'members',     label: 'Members',      icon: ShieldCheck },
  { id: 'clans',       label: 'Clans',        icon: Users },
  { id: 'seats',       label: 'Seats',        icon: Armchair },
  { id: 'matches',     label: 'Matches',      icon: Swords },
];

export function TournamentSetup() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  // View-as-aware copy of `me` — every gate/scope decision below reads THIS,
  // not the raw query result, so a founder previewing a lower role or an
  // "org admin (scoped)" simulation actually changes what this cockpit
  // shows. See lib/viewAs.ts's useEffectiveMe: identity no-op whenever no
  // view-as is active, so real (non-previewing) behaviour is unchanged.
  const effectiveMe = useEffectiveMe(me);
  const { data: orgs } = useQuery({ queryKey: ['orgs'], queryFn: api.orgs });
  const { data: comps } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedCompId, setSelectedCompId] = useState('');
  const [activeTool, setActiveTool] = useState<StepId>('org');
  // Bumped whenever a checklist jump targets the create-form / stage-composer
  // area — CompPane/CompetitionCreateForm use this to force the collapsed
  // create form open (or swap to the active comp's edit form) rather than
  // landing the highlight on a still-collapsed "+ New Competition" button.
  const [compFocusNonce, setCompFocusNonce] = useState(0);
  // Bumped whenever a jump targets Discord binding — that control lives
  // inside OrgPane's collapsed "Founder Settings" section, which otherwise
  // never opens on its own just because we scrolled to it.
  const [orgDiscordOpenNonce, setOrgDiscordOpenNonce] = useState(0);

  // Org-admin de-gate: a non-founder/platform-admin org manager only sees
  // THEIR orgs and those orgs' comps in these selectors — GET /orgs already
  // scopes to the caller's own memberships (any role) server-side, this
  // narrows further to ORG_ADMIN/ORG_OWNER only (a plain ORG_MEMBER/VIEWER
  // shouldn't land in this cockpit at all). managedOrgIds returns 'all' for
  // founder/platform-admin — unfiltered, unchanged existing behaviour.
  // Reads effectiveMe: viewing-as-org-admin-of-X narrows this to X ONLY.
  const managedIds = managedOrgIds(effectiveMe);
  const orgList = managedIds === 'all'
    ? (orgs ?? [])
    : (orgs ?? []).filter(o => managedIds.includes(o.id.replace(/^ORG#/, '')));
  const compList = managedIds === 'all'
    ? (comps ?? [])
    : (comps ?? []).filter(c => managedIds.includes((c.orgId ?? '').replace(/^ORG#/, '')));
  const activeOrgId = selectedOrgId || orgList[0]?.id || '';
  const activeComp = selectedCompId
    ? compList.find(c => c.id === selectedCompId)
    : compList[0];
  const activeCompId = activeComp?.id || '';

  const { data: clans } = useQuery({
    queryKey: ['competitionClans', activeCompId],
    queryFn: () => api.competitionClans(activeCompId),
    enabled: !!activeCompId,
  });
  const { data: matches } = useQuery({
    queryKey: ['matches', activeCompId],
    queryFn: () => api.matches(activeCompId),
    enabled: !!activeCompId,
  });

  const totalSeats = (clans ?? []).reduce((sum, c) => sum + (c.seatCount ?? 0), 0);

  // Go-live + org checklists (Phase 4a — transplanted off the retired modal
  // wizards; see lib/useCompChecklist.ts / lib/useOrgChecklist.ts). Pinned to
  // the SAME org/comp the anchor row above already resolves, so the strip
  // never tracks a different competition than the one the Status box shows.
  const compChecklist = useCompChecklist(activeOrgId || undefined, activeCompId || undefined);
  const orgChecklist = useOrgChecklist(activeOrgId || undefined, canWrite(effectiveMe?.role));
  // Shared by BOTH CompetitionLifecycleControls mounts below (anchor row +
  // global bottom-bar copy) — computed once so they can never drift apart.
  const hasStagePipeline = compChecklist.items.find(i => i.id === 'stages')?.done ?? false;

  // Compute "done" badges per tool for the tab-strip indicator dots — org/
  // competition/clans checkpoints now come straight off the checklists above
  // (single source of truth); members/seats/matches are unchanged.
  const doneMap: Record<StepId, boolean> = {
    org:         orgChecklist.items.every(i => i.done),
    competition: compChecklist.items.filter(i => i.tab === 'competition').every(i => i.done),
    members:     false,
    clans:       compChecklist.items.find(i => i.id === 'clans')?.done ?? false,
    seats:       totalSeats > 0,
    matches:     (matches?.length ?? 0) > 0,
  };

  const tools: Tool[] = useMemo(
    () => TOOLS_META.map(t => ({
      id: t.id,
      label: t.label,
      icon: t.icon,
      done: doneMap[t.id],
    })),
    [doneMap.org, doneMap.competition, doneMap.members, doneMap.clans, doneMap.seats, doneMap.matches], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Phase 4b: fire the edge-spark flourish (lib/sectionSparkFlourish.ts) every
  // time a highlight actually lands on a section — additive on top of
  // sectionHighlight.tsx's own scroll/pulse/focus, never a replacement for
  // it. Stable across renders so useSectionHighlightController's own
  // highlightNow identity doesn't churn on every TournamentSetup re-render.
  const onSectionHighlight = useCallback((_id: string, el: HTMLElement) => {
    fireEdgeSparkFlourish(el);
  }, []);

  // The on-page "over here" mechanism — scroll-to-focal-position + brief
  // pulse + auto-focus, shared by every checklist item and the "Guide me"
  // nudge below. See lib/sectionHighlight.tsx's header comment for the full
  // mechanism; Pass 2's spark extension point is wired via onSectionHighlight
  // above.
  const highlight = useSectionHighlightController(onSectionHighlight);

  // The ONE jump function every entry point funnels through: the strip's own
  // item clicks, GuideMeLauncher's "→ Next" nudge (wired in below), and the
  // `?jump=` URL param a cross-page GuideMeLauncher (e.g. Organizations.tsx)
  // falls back to when it can't call this closure directly.
  const performJump = (item: JumpableItem) => {
    if (item.externalHref) { navigate(item.externalHref); return; }
    if (item.tab) setActiveTool(item.tab as StepId);
    // 'rules' and 'publicPage' deliberately carry no `tab` of their own (see
    // useCompChecklist's header comment — neither should gate the Competition
    // tab's "done" dot), but their controls (the edit-mode Rules textarea,
    // the inline CompPublicPageEditor) only exist on that tab, so force the
    // switch here by id instead.
    else if (item.id === 'rules' || item.id === 'publicPage') setActiveTool('competition');
    if (item.id === 'created' || item.id === 'stages' || item.id === 'rules') setCompFocusNonce(n => n + 1);
    if (item.sectionId === 'org-discord') setOrgDiscordOpenNonce(n => n + 1);
    if (item.sectionId) highlight.highlightNow(item.sectionId);
  };

  // Cross-page arrival — GuideMeLauncher mounted elsewhere (Organizations.tsx)
  // encodes its target as `?jump=<tab>:<sectionId>`; decode once on mount,
  // perform the same jump a local click would, then scrub the param (same
  // "read once, then scrub" idiom the old `?guide=` deep link used).
  useEffect(() => {
    const jump = params.get('jump');
    if (jump) {
      performJump({ id: '', ...decodeSetupJump(jump) });
      const next = new URLSearchParams(params);
      next.delete('jump');
      setParams(next, { replace: true });
    }
    // Mount-only — deliberately not reacting to later param/closure changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // T2a: "Guide me" is always-on for founder/admin — same visibility
  // rule the Sidebar already uses for this page's own nav entry (isAdminRole).
  const canGuide = isAdminRole(effectiveMe?.role);

  // Org-admin de-gate: an ORG_ADMIN/ORG_OWNER of at least one org gets into
  // the cockpit too — scoped to their own org(s)/comp(s) via orgList/compList
  // above. Founder/admin (canWrite/isAdminRole) behaviour is unchanged.
  // Reads effectiveMe: viewing-as-member (no org-admin sim) correctly hits
  // "Admin access required" below; viewing-as-org-admin-of-X gets in, scoped
  // to X only.
  const isOrgManager = isOrgManagerOfAny(effectiveMe);
  const hasSetupAccess = !!me && (canWrite(effectiveMe?.role) || isOrgManager);

  // Global bottom action-bar registration (Lisa, 2026-07-19) — the SAME
  // CompetitionLifecycleControls element as the anchor row's Status box
  // below, mounted a SECOND time via usePageActionNode so activating from
  // the bar behaves identically (same activeComp/role/me/onChanged). This
  // replaces the page's old hardcoded fixed bottom bar — the bar itself,
  // its `--activation-bar-h` reservation, and its HelpFab corner are all
  // provider-owned now (lib/pageActions.tsx), not local to this page.
  // Registered as `null` when the access gate below fails, so a blocked
  // visitor never gets a bar entry either (see that file's role-awareness
  // contract) — hook must sit before the early return (Rules of Hooks).
  const lifecycleBarNode = useMemo(
    () => (
      <CompetitionLifecycleControls
        activeComp={activeComp}
        role={effectiveMe?.role}
        orgId={activeComp?.orgId}
        me={effectiveMe}
        hasStagePipeline={hasStagePipeline}
        onChanged={() => qc.invalidateQueries({ queryKey: ['competitions'] })}
      />
    ),
    [activeComp, effectiveMe, hasStagePipeline, qc],
  );
  usePageActionNode('setup-lifecycle', hasSetupAccess ? lifecycleBarNode : null);

  if (!hasSetupAccess) {
    // The retired modal used to give a helper SOME guided access here (its
    // own internal per-step gates showed read-only explainers in place of
    // the real forms). With the modal gone there is no page for a "Guide me"
    // nudge to jump within — this cockpit is still canWrite/org-manager-gated
    // exactly as it was before that modal ever existed (plus the org-admin
    // widen above). Deliberately no GuideMeLauncher on this dead end any
    // more; see the Phase 4a report for the write-up.
    return (
      <div>
        <Card className="text-center"><p className="text-ink-secondary">Admin access required.</p></Card>
      </div>
    );
  }

  // View-as-aware: drives OrgPane's "Founder Settings" section (billing,
  // Discord binding, role policy, formats) — a founder previewing a lower
  // role or an org-admin-scoped sim must NOT see founder-only controls.
  // Platform-tier bypass routed through isPlatformOrgManager (2026-07-24,
  // dormant platform-admin lockout closure) — the contract is founder OR
  // (platform) admin, not founder-only; a raw role==='founder' check locked
  // platform Admins out of surfaces the backend already lets them manage.
  const isFounder = isPlatformOrgManager(effectiveMe);

  const inspectorContent = inspectorFor(activeTool, {
    orgName: orgList.find(o => o.id === activeOrgId)?.name,
    compName: activeComp?.name,
    compStatus: activeComp?.status,
    clanCount: clans?.length ?? 0,
    seatCount: totalSeats,
    matchCount: matches?.length ?? 0,
  });

  // Org checklist strip (Organisation tab only) — unchanged Phase-4a
  // mechanism; it no longer shares a render slot with the comp checklist,
  // which moved inline per-row (Setup-page progress redesign, 2026-07-16 —
  // see CompPane/CompListRow below).
  const orgStripItems: StripItem[] = orgChecklist.items.map(i => ({ id: i.id, label: i.label, done: i.done, detail: i.detail }));
  // Final step (Lisa, 2026-07-19): the org strip read "4/4 READY" — all green
  // — while the selected comp sat PENDING right below it, so the process
  // LOOKED finished one click before it was. Append "Activate comp" as the
  // strip's closing step, scoped to the SAME activeComp the anchor row's
  // Status/Activate controls already track (accepted scope-mix: the strip
  // tail is contextual to the selected comp, exactly like the row under it).
  // Clicking it pulses the Activate button (the 'status-activate' anchor).
  if (activeComp) {
    orgStripItems.push({
      id: 'activateComp',
      label: 'Activate comp',
      done: activeComp.status === 'LIVE',
      detail: activeComp.status === 'LIVE' ? 'live' : (activeComp.status ?? 'pending').toLowerCase(),
    });
  }
  const orgStripItemsByKey = new Map<string, OrgChecklistItem>(orgChecklist.items.map(i => [i.id, i]));

  // Comp checklist detail — pinned to activeCompId (the SAME comp the Status
  // box / anchor row already track), rendered by CompPane inline under
  // whichever row is currently selected, instead of a page-top global strip.
  // A global strip read as "this org has one competition" the moment it had
  // 2+ (Lisa's PO feedback) — each row now carries its own N/5 (CompProgressDots)
  // and the SAME 5-item detail (reusing SetupProgressStrip verbatim, jump→
  // pulse→spark included) opens under whichever row you're working on.
  const compStripItems: StripItem[] = compChecklist.items.map(i => ({ id: i.id, label: i.label, done: i.done, detail: i.detail }));
  const compStripItemsByKey = new Map<string, CompChecklistItem>(compChecklist.items.map(i => [i.id, i]));
  const handleCompJump = (stripItem: StripItem) => {
    const full = compStripItemsByKey.get(stripItem.id);
    if (full) performJump(full);
  };

  return (
    <SectionHighlightProvider value={highlight}>
      {/* `--activation-bar-h` + the fixed bottom bar itself are now owned
          globally by PageActionsProvider/ActivationBarSpacer (Layout.tsx,
          lib/pageActions.tsx) — this page just registers its lifecycle
          controls into it (usePageActionNode above) instead of rendering
          its own local bar/var/padding, so SaveBar stacks above it exactly
          as before, on this page and every other page that grows a bar. */}
      <div
        // md:ml clears the attached tool rail (64px strip glued to the nav
        // sidebar's right edge — ToolingShell's fixed nav, Lisa's placement
        // v2) so the whole page, header included, sits right of it.
        className="md:ml-[72px]"
      >
        <header className={cn('mb-6 flex items-center justify-between gap-3', SETTLE)} style={settleDelay(0)}>
          <div className="flex items-center gap-3">
            <Rocket className="h-7 w-7 text-accent-500" strokeWidth={1.6} />
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Tournament Setup</h1>
              <p className="text-sm text-ink-secondary">End-to-end tournament scaffolding</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Presence face-pile — keyed to the selected competition (the
                comp-scoped resourceKey, e.g. 'COMP#abc123'), since that's the
                unit two admins are most likely to collide on inside this
                cockpit. Awareness only — renders nothing when it's just you. */}
            <PresenceBar resourceKey={activeCompId || undefined} />
            {/* T3 (Lisa, 2026-07-16): "Guide me" is now a plain nudge to the
                first unfinished checklist item — no modal. Tracks the active
                tool-strip tab the same way it always has: Organisation opens
                the org track, every other tab keeps the comp track. */}
            {canGuide && (
              <GuideMeLauncher
                orgId={activeOrgId || undefined}
                compId={activeCompId || undefined}
                track={activeTool === 'org' ? 'org' : 'comp'}
                onJump={performJump}
              />
            )}
          </div>
        </header>

        {/* Visible across every step — the cockpit-level orientation. */}
        <div className={SETTLE} style={settleDelay(1)}>
          <HelperCard
            id="tournament-setup-intro"
            title="What this cockpit does"
            intro="Six steps, worked top to bottom or jumped to directly: organisation → competition → members → clans → seats → matches. Each step's tool sits in the strip on the left."
            points={[
              { text: 'A competition’s stages (seeding, round robin, playoffs…) each carry their own settings — edit a stage while it’s still pending; it locks once started.' },
              { text: 'Activate, in the Status box below, takes the competition live. Team size locks at that point.' },
            ]}
            className="mb-4 max-w-4xl"
          />
        </div>

        {/* Progress strip — pinned, Organisation tab only now (Phase 4a's
            original global strip). The comp go-live checklist moved inline,
            per-row, into the Competition tab's own list (see CompPane) — a
            page-top strip tied to "whichever comp happens to be active" read
            as "there's only one competition" once an org had 2+. */}
        {activeTool === 'org' && (
          <div className={SETTLE} style={settleDelay(2)}>
            <SetupProgressStrip
              mode="org"
              items={orgStripItems}
              onJump={stripItem => {
                // The appended "Activate comp" step isn't an org-checklist
                // item — it points at the anchor row's Activate control.
                if (stripItem.id === 'activateComp') { highlight.highlightNow('status-activate'); return; }
                const full = orgStripItemsByKey.get(stripItem.id);
                if (full) performJump(full);
              }}
            />
          </div>
        )}

        {/* Selectors — workspace anchor row (+ inline activation, not a step).
            The Competition dropdown is gone — the Competition tab's own list
            IS the picker now (click a row to select it); Organisation keeps
            its selector unchanged. */}
        <div className={cn('mb-4 grid max-w-4xl gap-3 sm:grid-cols-2', SETTLE)} style={settleDelay(3)}>
          <div>
            <label className="label">Organisation</label>
            <select value={activeOrgId} onChange={e => setSelectedOrgId(e.target.value)} className="input w-full">
              {orgList.length === 0 && <option value="">No orgs yet</option>}
              {orgList.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div
            ref={highlight.registerSection('status-activate')}
            className={cn('rounded-lg', highlight.isHighlighted('status-activate') && 'section-pulse')}
          >
            <label className="label">Status</label>
            {/* Extracted to CompetitionLifecycleControls (Lisa, 2026-07-19)
                so the SAME status/Activate/End/Advance cluster can render a
                second time, unchanged, in the global bottom action bar
                (usePageActionNode('setup-lifecycle', …) above) — one
                implementation, two mount points, no duplicated markup. */}
            <CompetitionLifecycleControls
              activeComp={activeComp}
              role={effectiveMe?.role}
              orgId={activeComp?.orgId}
              me={effectiveMe}
              hasStagePipeline={hasStagePipeline}
              onChanged={() => qc.invalidateQueries({ queryKey: ['competitions'] })}
            />
          </div>
        </div>

        <div className={SETTLE} style={settleDelay(4)}>
          <ToolingShell
            tools={tools}
            activeId={activeTool}
            onActiveChange={id => setActiveTool(id as StepId)}
            inspector={inspectorContent}
            inspectorTitle={TOOLS_META.find(t => t.id === activeTool)?.label}
          >
            {activeTool === 'org' && (
              <OrgPane
                hasOrg={orgList.length > 0}
                org={orgList.find(o => o.id === activeOrgId)}
                me={effectiveMe ?? me}
                discordOpenSignal={orgDiscordOpenNonce}
              />
            )}
            {activeTool === 'competition' && (
              <CompPane
                comps={compList}
                orgId={activeOrgId}
                activeCompId={activeCompId}
                onSelectComp={setSelectedCompId}
                onCreated={() => qc.invalidateQueries({ queryKey: ['competitions'] })}
                focusNonce={compFocusNonce}
                detailItems={compStripItems}
                onJumpDetail={handleCompJump}
              />
            )}
            {activeTool === 'members' && <CompetitionMembersPanel compId={activeCompId} orgId={activeOrgId} />}
            {activeTool === 'clans' && <ClansPane compId={activeCompId} count={clans?.length ?? 0} isFounder={isFounder} />}
            {activeTool === 'seats' && <SeatsPane compId={activeCompId} />}
            {activeTool === 'matches' && <MatchesPane compId={activeCompId} />}
          </ToolingShell>
        </div>
      </div>
    </SectionHighlightProvider>
  );
}

// ── Panes ─────────────────────────────────────────────────────────────────────

function PaneHeading({ title, hint, help }: { title: string; hint?: string; help?: string }) {
  return (
    <div className="mb-5">
      <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      {hint && <p className="mt-1 text-sm text-ink-secondary">{hint}</p>}
      {help && (
        <HelpText>
          <p className="mt-1.5 text-xs text-ink-meta">{help}</p>
        </HelpText>
      )}
    </div>
  );
}

function OrgPane({
  hasOrg,
  org,
  me,
  discordOpenSignal,
}: {
  hasOrg: boolean;
  org?: OrganizationSummary;
  /** The CALLER passes effectiveMe here (view-as-aware) — isFounder below
   *  is therefore already view-as-correct without any hook of its own. */
  me: Me;
  /** Bump to force the "Founder Settings" section open + pulse the Discord
   * binding control inside it — see TournamentSetup's own header comment. */
  discordOpenSignal?: number;
}) {
  const qc = useQueryClient();
  const highlight = useSectionHighlightContext();
  // Platform-tier bypass routed through isPlatformOrgManager (2026-07-24,
  // dormant platform-admin lockout closure) — see the header comment on the
  // caller's own `isFounder` (this component receives the view-as-aware `me`).
  const isFounder = isPlatformOrgManager(me);
  // ORG_VIEWER folds into Member on the single role spine (2026-07-24) — an
  // unset membership role now defaults to ORG_MEMBER, not the retired viewer tier.
  const currentRole = org?.membershipRole ?? 'ORG_MEMBER';
  const refreshOrgs = () => qc.invalidateQueries({ queryKey: ['orgs'] });

  return (
    <div>
      <PaneHeading
        title={hasOrg ? org?.name ?? 'Organisation ready' : 'Create an organisation'}
        hint={hasOrg
          ? 'Everything about this organisation — people, requests, clans, governance, public page — managed here.'
          : 'Every tournament needs an organisation to live inside. Create one to continue.'}
        help="Orgs are the top-level container — competitions, clans, and the people who run them all belong to one."
      />
      {hasOrg && org ? (
        <div className="space-y-4">
          <div
            ref={highlight?.registerSection('org-identity')}
            className={cn('rounded-xl', highlight?.isHighlighted('org-identity') && 'section-pulse')}
          >
            <OrgPanel
              orgId={org.id}
              org={org}
              role={org.membershipRole ?? undefined}
            />
          </div>
          <OrgStatsPanel orgId={org.id} />
          <IncomingRoleRequestsPanel orgId={org.id} isFounder={isFounder} />
          <CollapsibleSection
            title="Notifications & Delegates"
            hint="Appoint delegates to absorb routine pings — role requests and billing always come to you."
            icon={BellRing}
          >
            <NotificationDelegatesPanel orgId={org.id} />
          </CollapsibleSection>
          {/* Bottom-bar "Add member" (OrgMembersPanel's own usePageActions
              call) scrolls+pulses here via highlightNow('org-members'). */}
          <div
            ref={highlight?.registerSection('org-members')}
            className={cn('rounded-xl', highlight?.isHighlighted('org-members') && 'section-pulse')}
          >
            <OrgMembersPanel
              orgId={org.id}
              currentUserId={me.discordId ?? ''}
              isFounder={isFounder}
              currentRole={currentRole}
            />
          </div>
          {/* Bottom-bar "Promote to owner" (OrgOwnerBoardPanel's own
              usePageActions call) scrolls+pulses+auto-focuses the member
              picker here via highlightNow('org-ownerboard'). */}
          <div
            ref={highlight?.registerSection('org-ownerboard')}
            className={cn('rounded-xl', highlight?.isHighlighted('org-ownerboard') && 'section-pulse')}
          >
            <OrgOwnerBoardPanel
              orgId={org.id}
              currentUserId={me.discordId ?? ''}
              isFounder={isFounder}
              currentRole={currentRole}
              foundingOwnerDiscordId={org.ownerDiscordId ?? ''}
            />
          </div>
          <OrgProposalsPanel
            orgId={org.id}
            currentUserId={me.discordId ?? ''}
            isFounder={isFounder}
            currentRole={currentRole}
          />
          <OrgClansPanel orgId={org.id} canManage={true} isFounder={isFounder} />
          <ClanCreateRequestsPanel orgId={org.id} canManage={currentRole === 'ORG_OWNER' || currentRole === 'ORG_ADMIN' || isFounder} />
          <div
            ref={highlight?.registerSection('org-publicpage')}
            className={cn('rounded-xl', highlight?.isHighlighted('org-publicpage') && 'section-pulse')}
          >
            <OrgPublicPageEditor
              orgId={org.id}
              slug={org.slug}
              orgName={org.name}
              accentColor={org.accentColor}
              createdAt={org.createdAt}
            />
          </div>
          {isFounder && (
            <CollapsibleSection
              title="Founder Settings"
              hint="Platform-level controls for this org — billing, Discord binding, role policy, formats."
              icon={Shield}
              openSignal={discordOpenSignal}
            >
              <div className="space-y-4">
                <FounderBillingControls isFounder={isFounder} orgId={org.id} onDone={refreshOrgs} />
                <div
                  ref={highlight?.registerSection('org-discord')}
                  className={cn('rounded-xl', highlight?.isHighlighted('org-discord') && 'section-pulse')}
                >
                  <GuildConnectionPanel me={me} orgId={org.id} />
                </div>
                <RolePolicySection isFounder={isFounder} />
                {/* EnabledFormatsPanel (founder per-org format grants) removed
                    2026-07-19 (Lisa): it only ever offered SWISS + GROUP_STAGE,
                    and both are STANDARD_FORMATS for everyone now (backend
                    orgAllowedFormats + CompetitionCreateForm's base set) — the
                    tick granted nothing. Organization.enabledFormats + its
                    PATCH endpoint stay for future bespoke formats. */}
              </div>
            </CollapsibleSection>
          )}
        </div>
      ) : (
        <div
          ref={highlight?.registerSection('org-identity')}
          className={cn('inline-block rounded-xl', highlight?.isHighlighted('org-identity') && 'section-pulse')}
        >
          <Link to="/orgs" className="btn-primary inline-flex items-center gap-1.5">
            <Building2 className="h-4 w-4" /> Create organisation
          </Link>
        </div>
      )}
    </div>
  );
}

function CompPane({
  comps,
  orgId,
  activeCompId,
  onSelectComp,
  onCreated,
  focusNonce,
  detailItems,
  onJumpDetail,
}: {
  comps: Competition[];
  orgId: string;
  activeCompId: string;
  onSelectComp: (id: string) => void;
  onCreated: () => void;
  /** Bumped by the go-live checklist's "Competition created"/"Stages
   * composed" jump (TournamentSetup's `compFocusNonce`) — see that file's
   * header comment. */
  focusNonce?: number;
  /** The active comp's 5-item go-live checklist (TournamentSetup's
   * `compStripItems`, derived off the SAME useCompChecklist(activeOrgId,
   * activeCompId) the Status box tracks) — rendered inline under whichever
   * row is currently selected, scoped unambiguously to THAT comp. */
  detailItems: StripItem[];
  onJumpDetail: (item: StripItem) => void;
}) {
  const highlight = useSectionHighlightContext();
  // comp.orgId is stored BARE (no ORG# prefix); the org selector value is
  // prefixed. Compare normalized values on BOTH sides — some legacy rows were
  // written with the ORG#-prefixed value (a fixed backend bug, IRL2 orgId
  // inconsistency) and would otherwise silently vanish from this list.
  const bareOrgId = orgId.replace(/^ORG#/, '');
  const orgComps = bareOrgId
    ? comps
        .filter(c => (c.orgId ?? '').replace(/^ORG#/, '') === bareOrgId)
        .sort((a, b) => {
          if (a.status !== b.status) {
            if (a.status === 'LIVE') return -1;
            if (b.status === 'LIVE') return 1;
          }
          return b.createdAt - a.createdAt;
        })
    : [];
  const liveCount = orgComps.filter(c => c.status === 'LIVE').length;

  // Edit-while-PENDING: clicking a row's Edit affordance swaps the create-form
  // area below the list for this comp's edit form (one at a time — editing
  // and creating are mutually exclusive, and this reads cleaner than an
  // in-row expansion given how many rows an org can have).
  const [editingComp, setEditingComp] = useState<Competition | null>(null);
  // Phase 4a (Lisa, 2026-07-16 — modal retirement): "+ New Competition" is no
  // longer a wizard launcher — CompetitionCreateForm mounts directly below,
  // in its own collapsed state (its collapsed render IS the "+ New
  // Competition" button). `createOpenTrigger` force-expands it when the
  // go-live checklist jumps to "Competition created"/"Stages composed" and
  // there's no existing comp to open the EDIT form for instead.
  const [createOpenTrigger, setCreateOpenTrigger] = useState(0);

  const activeOrgComp = orgComps.find(c => c.id === activeCompId);
  const focusNonceRef = useRef(focusNonce);
  useEffect(() => {
    if (focusNonce === undefined || focusNonce === focusNonceRef.current) return;
    focusNonceRef.current = focusNonce;
    // The checklist's "Stages composed" means the ACTIVE competition's real
    // stage pipeline if one already exists — open its edit form (the pencil's
    // one home for comp structure) rather than the blank create form.
    if (activeOrgComp) setEditingComp(activeOrgComp);
    else setCreateOpenTrigger(n => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNonce]);

  return (
    <div>
      <PaneHeading
        title="Competition"
        hint={orgComps.length > 0
          ? `${orgComps.length} competition${orgComps.length === 1 ? '' : 's'} in this org (${liveCount} live). Click one to work on it, or create another below.`
          : 'No competitions yet in this org. Use the form to spin one up.'}
        help="A competition is one tournament instance — its own format, dates, and stage pipeline. Pencil-edit an existing row, or compose a new one below."
      />
      {orgComps.length > 0 && (
        <div className="mb-5 space-y-2">
          {orgComps.map(c => (
            <div key={c.id}>
              <CompListRow
                comp={c}
                orgId={orgId}
                isActive={activeCompId === c.id}
                onSelect={() => onSelectComp(c.id)}
                onEdit={() => setEditingComp(c)}
              />
              {/* Detailed 5-item go-live checklist — opens under whichever row
                  is currently active, scoped unambiguously to THAT comp. Same
                  SetupProgressStrip render Phase 4 pinned to the page top;
                  only its mount point moved (jump→pulse→spark unchanged,
                  wired via TournamentSetup's onJumpDetail → performJump). */}
              {activeCompId === c.id && (
                <div className="mt-1.5 pl-1 pr-1">
                  <SetupProgressStrip mode="comp" items={detailItems} onJump={onJumpDetail} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Stage Pipeline (T2): StagePipelinePanel's only mount is now inside
          CompetitionCreateForm's edit mode (the pencil is the one home for
          comp structure) — no standalone "+ Add stage pipeline" affordance
          here any more. Click a row's Edit to reach it. */}
      {/* Distinct keys force a remount between modes — same component type at
          the same position would otherwise keep the create form's collapsed
          open-state and stale field state (useState initializers run once). */}
      {editingComp ? (
        <CompetitionCreateForm
          key={`edit-${editingComp.id}`}
          editComp={editingComp}
          onDone={() => { setEditingComp(null); onCreated(); }}
          onCancel={() => setEditingComp(null)}
        />
      ) : (
        // Phase 4a: "+ New Competition" IS this form now — no modal, no
        // separate wizard step. CompetitionCreateForm's own collapsed render
        // is the "+ New Competition" button; a successful create selects the
        // freshly created comp so the checklist's next items (stages/clans/
        // activate) immediately target it.
        <CompetitionCreateForm
          key="create"
          orgId={orgId || undefined}
          onDone={created => { onCreated(); if (created?.id) onSelectComp(created.id); }}
          openTrigger={createOpenTrigger}
        />
      )}

      {/* Public page (setup-consolidation, 2026-07-18): mirrors OrgPane's
          org-publicpage wrapper exactly — moved off the old /me/comp/:compId
          route so all comp setup (create/edit, stages, rules, public page)
          lives in one place. Guarded on activeOrgComp (not just activeCompId)
          so it only renders once there's a real, selected competition in
          THIS org to author a page for — same org-manager gate as the create/
          edit form above, since this whole tab is already canWrite/org-manager
          gated at the page root. */}
      {activeOrgComp && (
        <div
          ref={highlight?.registerSection('comp-publicpage')}
          className={cn('mt-4 rounded-xl', highlight?.isHighlighted('comp-publicpage') && 'section-pulse')}
        >
          <CompPublicPageEditor compId={activeOrgComp.id} />
        </div>
      )}
    </div>
  );
}

// Terse "Xd Yh" duration formatter for the run-time line — no seconds/minutes,
// this is a glanceable admin readout, not a stopwatch.
function formatDurationShort(ms: number): string {
  const clamped = Math.max(0, ms);
  const days = Math.floor(clamped / 86_400_000);
  const hours = Math.floor((clamped % 86_400_000) / 3_600_000);
  return `${days}d ${hours}h`;
}

// One competition row in CompPane's list — split out from CompPane's own
// render so it can hold its own useQuery hook (hooks illegally sitting
// inside a .map() inside a component body is the thing this avoids).
// Enrichment beyond the original name/format/status line:
//   - stage chip (SEEDING/ROUND_ROBIN/PLAYOFFS, or "not started")
//   - live clan count (shares the ['competitionClans', id] query key already
//     used at the page root for the active comp, so react-query dedupes)
//   - a terse run-time readout: "live Xd Yh" once LIVE, "created <date>"
//     while PENDING, and for CLOSED — no endedAt is stamped anywhere in the
//     backend today, so this always falls back to the created date too.
//   - CompProgressDots (Setup-page progress redesign, 2026-07-16): its own
//     N/5 go-live read, via useCompChecklist(orgId, c.id) called PER ROW.
//     The comp-list response has no per-comp stage/enrol counts (Competition
//     only carries the legacy single `stage` marker, not a compStages count,
//     and no clan-enrolment count) — genuinely missing from the list payload,
//     so this reuses the SAME hook the page-level checklist already calls,
//     just once per row. Its ['orgs']/['competitions'] queries dedupe for
//     free (same keys, already cached); its ['competitionClans', c.id] and
//     ['compStages', c.id] queries are the real per-row fetches — `enabled:
//     !!activeCompId` inside the hook itself makes them fire lazily as each
//     row mounts, no blocking N+1, fine for the handful of comps an org runs
//     at once. Frontend-only — no backend change needed.
function CompListRow({
  comp: c,
  orgId,
  isActive,
  onSelect,
  onEdit,
}: {
  comp: Competition;
  orgId: string;
  isActive: boolean;
  onSelect: () => void;
  onEdit: () => void;
}) {
  const { data: rowClans, isLoading: clansLoading } = useQuery({
    queryKey: ['competitionClans', c.id],
    queryFn: () => api.competitionClans(c.id),
  });
  const rowChecklist = useCompChecklist(orgId, c.id);

  const stageLabel = c.stage ? c.stage.replace(/_/g, ' ').toLowerCase() : 'not started';
  // Comp-summary count reads "teams" (Lisa's QA) — the entities enrolled are
  // clan rows, but from THIS competition's point of view each is fielding one
  // team; count/source is unchanged (one enrollment === one team).
  const teamCountLabel = clansLoading ? '—' : `${rowClans?.length ?? 0} team${(rowClans?.length ?? 0) === 1 ? '' : 's'}`;

  let runTimeLabel: string;
  if (c.status === 'LIVE') {
    runTimeLabel = `live ${formatDurationShort(Date.now() - (c.activatedAt ?? c.createdAt))}`;
  } else if (c.status === 'CLOSED') {
    // No endedAt field exists on the Competition row yet — fall back to created.
    runTimeLabel = `created ${new Date(c.createdAt).toLocaleDateString()}`;
  } else {
    runTimeLabel = `created ${new Date(c.createdAt).toLocaleDateString()}`;
  }

  // Progressive stage-aware lock (mirrors the backend PATCH gate): PENDING is
  // always editable; LIVE stays editable too while stage is null/'SEEDING'
  // (the round-robin/mid-stage hasn't started — teamSize itself locks
  // separately inside the edit form, at go-live). Only once stage moves past
  // SEEDING (ROUND_ROBIN/PLAYOFFS/anything else — fail-closed) or the comp
  // CLOSES does the row hard-lock — pencil disappears, a subtle Lock +
  // "setup locked" note in the muted meta line is the only trace it was ever
  // editable at all.
  const isEditable = c.status === 'PENDING'
    || (c.status === 'LIVE' && (c.stage == null || c.stage === 'SEEDING'));

  return (
    <div
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-lg bg-white/[0.04] px-3 py-2 transition hover:bg-white/[0.06]',
        isActive && 'bg-accent-500/10',
      )}
    >
      <button type="button" onClick={onSelect} className="min-w-0 flex-1 text-left">
        <p className="text-sm font-medium text-ink-primary">{c.name || c.id.replace('COMP#', '')}</p>
        <p className="flex items-center gap-1 text-xs text-ink-meta">
          <span>{c.teamSize}v{c.teamSize}{c.format ? ` · ${c.format}` : ''} · {runTimeLabel}</span>
          {!isEditable && (
            <span className="inline-flex items-center gap-0.5">
              · <Lock className="h-3 w-3" /> setup locked
            </span>
          )}
        </p>
        <p className="mt-1 flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-white/[0.06] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-meta">
            {stageLabel}
          </span>
          <span className="text-xs text-ink-meta">{teamCountLabel}</span>
          <CompProgressDots readyCount={rowChecklist.readyCount} totalCount={rowChecklist.totalCount} />
        </p>
      </button>
      <div className="flex shrink-0 items-center gap-1.5">
        {isEditable && (
          <button
            type="button"
            onClick={onEdit}
            title="Edit setup"
            className="inline-flex items-center gap-1 rounded p-1.5 text-ink-meta transition hover:bg-white/[0.06] hover:text-ink-primary"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        <StatusBadge status={c.status} />
      </div>
    </div>
  );
}

function ClansPane({ compId, count, isFounder }: { compId: string; count: number; isFounder: boolean }) {
  const highlight = useSectionHighlightContext();
  return (
    <div>
      <PaneHeading
        title="Clans in this competition"
        hint={compId
          ? `${count} team${count === 1 ? '' : 's'} enrolled — manage them below.`
          : 'Pick a competition above to manage its clans.'}
        help="Each enrolled clan fields one team for this competition — recruited by leaders or invited by admins."
      />
      {compId && (
        <div
          ref={highlight?.registerSection('clans-workbench')}
          className={cn('rounded-xl', highlight?.isHighlighted('clans-workbench') && 'section-pulse')}
        >
          <ClanWorkbench compId={compId} isFounder={isFounder} isAdminPlus={true} />
        </div>
      )}
      <div className="mt-4">
        <TransferLeadershipPanel />
      </div>
    </div>
  );
}

function SeatsPane({ compId }: { compId: string }) {
  return (
    <div>
      <PaneHeading
        title="Roster overview"
        hint={compId
          ? 'Who is in which team, across every enrolled clan — at a glance.'
          : 'Pick a competition above to view its roster.'}
      />
      <RosterOverviewPanel compId={compId} />
    </div>
  );
}

function MatchesPane({ compId }: { compId: string }) {
  return (
    <div>
      <PaneHeading
        title="Match schedule"
        hint={compId
          ? 'Every match in this competition, grouped by stage and round — expand a stage to review or enter results inline.'
          : 'Pick a competition above to manage its matches.'}
        help="Matches are scheduled clan-vs-clan series. Each one generates a Match Card the clan leaders track in Discord."
      />
      {compId && <MatchScheduleBoard compId={compId} />}
    </div>
  );
}

// ActivatePane removed — activation is the inline Status control on the
// anchor row; the wizard steps are pure build-out.

// ── Inspector content per tool ────────────────────────────────────────────────

function inspectorFor(
  step: StepId,
  ctx: {
    orgName?: string;
    compName?: string;
    compStatus?: string;
    clanCount: number;
    seatCount: number;
    matchCount: number;
  },
) {
  const map: Record<StepId, { title: string; body: string; stat?: string }> = {
    org:         { title: 'Why orgs exist',  body: 'Orgs are containers for related competitions. Members and admins are scoped to the org.', stat: ctx.orgName ? `Active: ${ctx.orgName}` : 'No org selected' },
    competition: { title: 'About competitions', body: 'A competition is one tournament instance: a fixed format (BO3/BO5), a date window, a set of clans.', stat: ctx.compName ? `${ctx.compName} · ${ctx.compStatus}` : '—' },
    members:     { title: 'Competition roles', body: 'Assigns competition-scoped roles — owners, admins, and members who can run this competition — separate from org membership.', stat: ctx.compName ? `${ctx.compName}` : 'No competition selected' },
    clans:       { title: 'Team enrollment',  body: 'Each enrolled clan fields one team for this competition. Clans are recruited by leaders or invited by admins.', stat: `${ctx.clanCount} team${ctx.clanCount === 1 ? '' : 's'} enrolled` },
    seats:       { title: 'Seat counts',      body: 'Seats define how many players each clan can field. Most clans use 4–8 seats matching team-size.', stat: `${ctx.seatCount} total` },
    matches:     { title: 'Match schedule',   body: 'Matches are scheduled clan-vs-clan series. Each generates a Match Card for live tracking.', stat: `${ctx.matchCount} scheduled` },
  };
  const e = map[step];
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-ink-primary">{e.title}</h3>
      <p className="text-sm leading-relaxed text-ink-secondary">{e.body}</p>
      {e.stat && (
        <div className="mt-4 rounded-lg bg-white/[0.04] px-3 py-2.5 text-xs text-ink-meta">
          <div className="font-medium uppercase tracking-wider">State</div>
          <div className="mt-1 font-mono text-ink-secondary">{e.stat}</div>
        </div>
      )}
    </div>
  );
}
